/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { role, Subtenant } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedSubtenantsQuery } from '@/hooks/query/usePaginatedSubtenantsQuery';
import { usePaginatedUserSubtenantsQuery } from '@/hooks/query/usePaginatedUserSubtenantsQuery';
import { LoadingSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/LoadingSubtenants';
import { NoSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/NoSubtenants';
import { useUserSubtenantListColumns } from '@/pages/Users/useUserSubtenantListColumns';

import './UserSubtenantsHierarchy.css';

export type UserSubtenantsHierarchyProps = {
  userId: string;
  handleUpdateUserSubtenantRole: (args: { subtenantId: string; userSubtenantRole: role }) => void;
  parentSubtenant?: Subtenant;
  nestingLevel?: number;
};

export function UserSubtenantsHierarchy({
  userId,
  parentSubtenant,
  handleUpdateUserSubtenantRole,
  nestingLevel = 0,
}: UserSubtenantsHierarchyProps) {
  const subtenantsQuery = usePaginatedSubtenantsQuery({
    type: parentSubtenant ? 'inMemory' : 'queryParams',
    params: { filters: { subtenantId: parentSubtenant?.id } },
  });

  const userSubtenantsQuery = usePaginatedUserSubtenantsQuery({
    type: parentSubtenant ? 'inMemory' : 'queryParams',
    params: { id: userId, filters: { subtenantId: parentSubtenant?.id } },
  });

  const columns = useUserSubtenantListColumns(handleUpdateUserSubtenantRole, userSubtenantsQuery);

  const loadingComponent = parentSubtenant ? <LoadingSubtenants /> : undefined;

  return (
    <div
      className={['user-update-subtenants-hierarchy', `nesting-level-${nestingLevel}`].join(' ')}
      style={{ width: '100%' }}
    >
      <QueryWrapper queryResult={subtenantsQuery} loadingComponent={loadingComponent}>
        {(subtenantsData) => {
          if (parentSubtenant && !subtenantsData.data.length) {
            return <NoSubtenants />;
          }

          return (
            <PaginatedTable
              data={subtenantsData}
              hidePaginationInfo
              onParamsChange={subtenantsQuery.updateQueryParams}
              rowKey={(subtenant) => subtenant.id}
              columns={columns}
              data-testid={`user-update-subtenants-hierarchy-${parentSubtenant?.id || 'root'}`}
              expandable={{
                // we have to explicitly set this to something other than 'children' or undefined
                // otherwise AntD ignores the fact that we have a custom expandedRowRender
                // and duplicates the rows (shows the children as it usually does plus the expandedRowRender)
                childrenColumnName: 'not-children',
                rowExpandable: (record) => !!record.children,
                // eslint-disable-next-line react/no-unstable-nested-components
                expandedRowRender: (record: Subtenant, _idx: number, indent: number, expanded: boolean) => {
                  if (!expanded) return null;
                  return (
                    <UserSubtenantsHierarchy
                      userId={userId}
                      parentSubtenant={record}
                      handleUpdateUserSubtenantRole={handleUpdateUserSubtenantRole}
                      nestingLevel={nestingLevel + indent}
                    />
                  );
                },
              }}
            />
          );
        }}
      </QueryWrapper>
    </div>
  );
}
